import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';
import {
  WorkspacesOrderedInfo,
  NewWorkspace,
  AddNewWorkspace,
  WorkspacesInfo,
  WorkspacesManageAccessInfo,
  UserInfo,
  Response,
  AvailableEntity,
  MoveTo
} from '../models';
import { SearchBarResponse } from '../models/workspaces.models';


@Injectable({ providedIn: 'root' })
export class WorkspaceService {

  constructor(
    private http: HttpClient,
    private toastService: ToastService) { }

    
  getWorkspacesInfo(object: WorkspacesOrderedInfo): Promise<{ workspaces: WorkspacesInfo[] }> {
    return this.http.get<{ workspaces: WorkspacesInfo[] }>(`${environment.apiUrl}/Workspaces-WorkspacesInformation`,
      {
        params: new HttpParams()
          .set('isShared', String(object.isShared))
          .set('key', object.key ?? '')
          .set('orderby', object.orderby ?? '')
          .set('page', String(object.page ?? 1))
          .set('pagesize', String(object.pagesize ?? 10))
      }
    ).toPromise();
  }

  addEntityToFollowed(idEntity: string, type: string): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/AddProjectToFollowed`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  async getInfoEntity(idParent: string, entityType: string): Promise<{ name: string }> {
    return this.http.get<{ name: string }>(`${environment.apiUrl}/Get-GetNewExecutionPlaceholderInfo`,
      {
        params: new HttpParams().set('entity-type', entityType).set('id-parent', idParent)
      }
    ).toPromise();
  }

  async getUserTree(entityType: string): Promise<{ root: AvailableEntity[] }> {
    return this.http.get<{ root: AvailableEntity[] }>(`${environment.apiUrl}/Get-GetUserTree`,
      {
        params: new HttpParams().set('entity-type', entityType)
      }
    ).toPromise();
  }

  async moveProjectOrItem(model: MoveTo): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/MoveProjectOrItem`,
      {
        ...model
      }
    ).toPromise();
  }

  async checkRequiredConfirmToMoveTo(model: MoveTo): Promise<Response> {
    return this.http.post<Response>(`${environment.apiUrl}/CheckRequiredConfirmToMoveTo`,
      {
        ...model
      }
    ).toPromise();
  }

  addEntityToFavourite(idEntity: string, type: string): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/AddEntityToFavourite`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  revertStatement(idEntity: string, type: string): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/RevertItemStatement`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  addNewProjectToWorkspace(entry: AddNewWorkspace): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/Workspaces-AddNewProject`,
      {
        id_related_workspace: entry.id_entity,
        name: entry.name,
        description: entry.description,
        shared: entry.shared
      }
    ).toPromise();
  }

  addNewWorkspace(object: AddNewWorkspace): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/Workspaces-AddNewWorkspace`,
      object
    ).toPromise();
    // Todo API return wrong error object
  }

 
  // todo: check this logic on share. why doesn't work???
  async addCollaboratorsToProject(users: UserInfo[], id: string, type: string) {
    const array = users.map(async user => await this.addUserToWorkspace(id, type, user.id));
    await Promise.all(array)
    .then(res => res)
    .catch(error => {
      throw error;
    });

  }

  addUserToWorkspace(idEntity: string, type: string, idUser: string): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/Workspaces-AddUserToWorkspace`,
      {
        id_entity: idEntity,
        type,
        id_user: idUser
      }
    ).toPromise();
  }

  changeEntityDescription(idEntity: string, description: string, type: string): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/ChangeEntityDescription`,
      {
        id_entity: idEntity,
        description,
        type
      }
    ).toPromise();
  }

  changeEntityName(idEntity: string, name: string, type: string): Promise<Response> {
    return this.http.post<Response>(`${environment.apiUrl}/ChangeEntityName`,
      {
        id_entity: idEntity,
        name,
        type
      }
    ).toPromise();
  }

  async deleteEntity(idEntity: string, type: string): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/DeleteEntity`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  async deleteEntityToFavourite(idEntity: string, type: string): Promise<NewWorkspace> {
    return this.http.post<NewWorkspace>(`${environment.apiUrl}/DeleteEntityToFavourite`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  deleteProjectToFollowed(idEntity: string, type: string): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/Workspaces-DeleteProjectToFollowed`,
      {
        id_entity: idEntity,
        type
      }
    ).toPromise();
  }

  deleteUserFromWorkspace(idEntity: string, type: string, idUser: string): Promise<{ response: NewWorkspace }> {
    return this.http.post<{ response: NewWorkspace }>(`${environment.apiUrl}/Workspaces-DeleteUserFromWorkspace`,
      {
        id_entity: idEntity,
        type,
        id_user: idUser
      }
    ).toPromise();
  }

  getSingleWorkspaceInfo(idWorkspace: string, columnName?: string, orderBy?: string): Promise<WorkspacesInfo> {
    let url: string;
    if (columnName && orderBy) {
      url = `${environment.apiUrl}/SingleWorkspace-WorkspaceInformation?key=${columnName}&orderby=${orderBy}`;
    } else {
      url = `${environment.apiUrl}/SingleWorkspace-WorkspaceInformation`;
    }
    return this.http.get<WorkspacesInfo>(url,
      {
        params: new HttpParams().set('id-workspace', idWorkspace)
      }
    ).toPromise();
  }

  async setNewWorkspaceData(entry: AddNewWorkspace): Promise<WorkspacesInfo> {
    const receivedEntry = {
      name: entry.name,
      type: entry.type,
      description: null,
      shared: true,
      collaborators: entry.collaborators,
    };
    try {
      const newWorkspace = (await this.addNewWorkspace(receivedEntry));
      this.toastService.showSuccess({
        description: `${receivedEntry.name} has been successfully added`,
        type: 'success'
      });
      const newWorkspaceInfo = (await this.getSingleWorkspaceInfo(newWorkspace.id_entity));
      if (receivedEntry.collaborators.length !== 0) {
        receivedEntry.collaborators.forEach(element => {
          this.addUserToWorkspace(newWorkspace.id_entity, receivedEntry.type, element.id);
        });
      }
      return newWorkspaceInfo;
    } catch (error) {
      throw error;
    }
  }

  async getEntityAccess(idUser: string): Promise<{ workspaces: WorkspacesManageAccessInfo[] }> {
    return this.http.get<{ workspaces: WorkspacesManageAccessInfo[] }>(`${environment.apiUrl}/GetEntityAccessByUser`,
      {
        params: new HttpParams().set('id-user', idUser)
      }
    ).toPromise();
   }

    // api/TopBar_GetSearchBarResponse
    getSearchBarItems(searchValue: string): Promise<{ searchBarResponseCategories: SearchBarResponse[] }> {
        return this.http.get<{ searchBarResponseCategories: SearchBarResponse[] }>(`${environment.apiUrl}/TopBar_GetSearchBarResponse`,
            {
                params: new HttpParams()
                    .set('searchValue', searchValue)
            }
        ).toPromise();
    }

}
